/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useSelector } from 'react-redux';
import { Roles } from '../enums/Roles';
import { SubmissionStatus } from '../../models/SubmissionStatus';
import { lockTimeoutMillis } from '../../settings';
import { selectAuthUserData } from '../../redux/features/auth/auth.slice';

const PermittedActions = {
  [Roles.SUBMISSION_DRAFTER]: {
    canCreate: () => true,
    canEdit: status => [SubmissionStatus.DRAFT.id, SubmissionStatus.VALID_DRAFT.id].includes(status),
    canDelete: status => [SubmissionStatus.DRAFT.id, SubmissionStatus.VALID_DRAFT.id].includes(status),
    canChangeStatus: submission => submission?.valid && SubmissionStatus.DRAFT.id === submission?.status,
    canSendToPackage: status => SubmissionStatus.VALID_READY_TO_SIGN === status,
    canRollbackToDraft: status =>
      [SubmissionStatus.READY_TO_SIGN.id, SubmissionStatus.VALID_READY_TO_SIGN.id].includes(status),
  },
  [Roles.SUBMISSION_SIGNER]: {
    canSendToSign: status => SubmissionStatus.READY_TO_SIGN.id === status,
    canChangeStatus: submission => SubmissionStatus.READY_TO_SIGN.id === submission?.status,
    canRollbackToDraft: status =>
      [SubmissionStatus.READY_TO_SIGN.id, SubmissionStatus.VALID_READY_TO_SIGN.id, SubmissionStatus.SIGNING_FAILED.id].includes(status),
    canRollbackToReadyToSign: status =>
      [SubmissionStatus.SIGNING_FAILED.id, SubmissionStatus.READY_TO_SEND.id, SubmissionStatus.READY_TO_PAY.id].includes(status),
  },
  [Roles.SUBMISSION_SENDER]: {
    canSend: status => SubmissionStatus.READY_TO_SEND.id === status,
    canChangeStatus: submission => SubmissionStatus.READY_TO_SEND.id === submission?.status,
    canRollbackToDraft: status =>
      [SubmissionStatus.READY_TO_SEND.id, SubmissionStatus.SENDING_FAILED.id].includes(status),
    canRollbackToReadyToSign: status =>
      [SubmissionStatus.READY_TO_SEND.id, SubmissionStatus.SENDING_FAILED.id].includes(status),
  },
  [Roles.END_USER_ADMIN]: {
    canDelete: status =>
      status &&
      ![SubmissionStatus.SENT_TO_SIGN.id, SubmissionStatus.SENT_TO_FILING.id, SubmissionStatus.SENT.id].includes(
        status,
      ),
  },
  [Roles.SUBMISSION_PAYER]: {
    canRollbackToDraft: status =>
      [SubmissionStatus.READY_TO_PAY.id, SubmissionStatus.RESIGNING_FAILED.id].includes(status),
    canRollbackToReadyToSign: status =>
      [SubmissionStatus.READY_TO_PAY.id, SubmissionStatus.RESIGNING_FAILED.id].includes(status),
  },
};

export default function useSubmissionActions() {
  const userData = useSelector(selectAuthUserData);
  const currentMsTimeStamp = new Date().getTime();
  const WITHOUT_PAYMENT = 'withoutPayment';

  const isLocked = submission =>
    submission?.lockUser !== null &&
    currentMsTimeStamp - new Date(submission?.modifiedDate).getTime() < lockTimeoutMillis &&
    submission?.lockUser !== userData?.sub;

  const canCreate = () => userData?.roles?.some(role => PermittedActions[role]?.canCreate?.());

  const canEdit = (submission) => {
    return (
      !isLocked(submission) && userData?.roles?.some(role => PermittedActions[role]?.canEdit?.(submission?.status))
    );
  };

  const canDelete = submission =>
    !isLocked(submission) && userData?.roles?.some(role => PermittedActions[role]?.canDelete?.(submission?.status));

  const canSendToSign = submission =>
    userData?.roles?.some(role => PermittedActions[role]?.canSendToSign?.(submission?.status));

  const canSendToPackage = submission =>
    userData?.roles?.some(role => PermittedActions[role]?.canSendToPackage?.(submission?.status));

  const canSendToFiling = submission =>
    userData?.roles?.some(role => PermittedActions[role]?.canSend?.(submission?.status));

  const canChangeStatus = submission =>
    userData?.roles?.some(role => PermittedActions[role]?.canChangeStatus?.(submission));

  const canSendWithoutPaying = submission => submission?.fees?.methodOfPayment === WITHOUT_PAYMENT;

  const canPay = submission => !canSendWithoutPaying(submission) && userData?.roles?.includes(Roles.SUBMISSION_PAYER);

  const canRollbackToDraft = submission =>
    userData?.roles?.some(role => PermittedActions[role]?.canRollbackToDraft?.(submission?.status)) &&
    !submission['paymentResponse.successful'];

  const canRollbackToReadyToSign = submission =>
    userData?.roles?.some(role => PermittedActions[role]?.canRollbackToReadyToSign?.(submission?.status)) &&
    !submission['paymentResponse.successful'];

  return {
    isLocked,
    canCreate,
    canEdit,
    canDelete,
    canSendToSign,
    canSendToPackage,
    canSendToFiling,
    canChangeStatus,
    canPay,
    canSendWithoutPaying,
    canRollbackToDraft,
    canRollbackToReadyToSign,
  };
}
